/**
 * PayoutCountryStep — asks the instructor where they are based, before the
 * wallet wizard makes any promise about payouts.
 *
 * Why this screen exists: onboarding never collects a country. `OnboardingStep1Profile`
 * captures photo, name, belt, degrees and academy, and the instructor agenda step
 * inserts its config with `class_value: null` — nothing writes `profiles.country`.
 * The only places that ask are the community-discovery location modals and the
 * profile editor, so for most instructors the field is NULL when they open the
 * wallet.
 *
 * `EarningsPage` gates payout setup on that field, so a NULL country used to end
 * the flow on a screen reading "We couldn't determine your country", whose only
 * way forward was to leave for the profile editor and come back. This step asks
 * the question in place instead, and the gate re-evaluates as soon as the answer
 * is saved.
 *
 * The full country list is offered, not just the payable one. An instructor
 * based in Brazil has to be able to answer truthfully and be told plainly that
 * payouts are not available to them yet — being unable to state your own
 * country is not a gate, it is a trap. The US-only note is shown before the
 * choice, not after it, so nobody discovers the restriction by hitting it.
 *
 * The gate itself is unchanged: `isPayoutCountrySupported` still accepts the
 * United States only. This screen changes how the instructor gets asked, never
 * who is allowed through.
 */

import React, { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useUpdatePayoutCountry } from '@/features/wallet/hooks/useUpdatePayoutCountry';
import { countries } from '@/utils/locationData';
import { SOSSIETY_RED } from '@/styles/brand';

// Tokens are duplicated from the wallet wizard rather than imported: `Earnings.tsx`
// keeps them module-private, and this screen has to sit inside that flow without
// a visible seam.
const C = {
  bg:       '#160C0C',
  surface:  '#281B1B',
  surface2: '#1F1313',
  border:   '#FFFFFF1A',
  text:     '#FFFFFF',
  muted:    '#8D8D8D',
  accent:   SOSSIETY_RED,
  error:    '#FF6B6B',
};

/**
 * How many rows the list renders at once. Enough to fill a phone screen several
 * times over, small enough that mounting them is free.
 */
const MAX_VISIBLE_COUNTRIES = 24;

export interface PayoutCountryStepProps {
  /** Current `profiles.country`, pre-selected when the instructor is correcting it. */
  initialCountry?: string | null;
  /** Called once the country is persisted and the profile caches are refreshed. */
  onSaved: () => void;
  /** Leaves payout setup without saving. */
  onDismiss: () => void;
}

export const PayoutCountryStep: React.FC<PayoutCountryStepProps> = ({
  initialCountry,
  onSaved,
  onDismiss,
}) => {
  const { t } = useI18n();
  const [selected, setSelected] = useState<string>(initialCountry?.toLowerCase() ?? '');
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return countries;
    return countries.filter((country) => country.label.toLowerCase().includes(needle));
  }, [query]);

  /**
   * Only the first slice is rendered, and the search box is how you reach the
   * rest.
   *
   * The list is 194 entries. Mounting every one as its own button — with a
   * per-row style object — cost real time on a phone and re-reconciled the
   * whole array on each keystroke, for a list nobody scrolls to the end of
   * anyway. The cap bounds every render, and the count line below says plainly
   * that more exist rather than letting the list look complete.
   *
   * Nothing is pinned to the top, deliberately. Surfacing the one payable
   * country next to a note saying only that country can be paid would be
   * telling the instructor what to answer, and this is the field their tax
   * paperwork hangs on.
   */
  const visibleCountries = useMemo(() => {
    const slice = matches.slice(0, MAX_VISIBLE_COUNTRIES);

    // The current answer is kept on screen when the cap alone would have cut
    // it. Reopening this step from the refusal screen is supposed to show the
    // instructor what they picked last time — and every country from Cambodia
    // onwards falls outside the default slice, so without this the affordance
    // would work for a handful of countries and silently vanish for the rest.
    //
    // But only when the cap is the reason it is missing. A search that does not
    // match the selection must render its own results untouched, including the
    // empty state: pulling the previous pick back into a filtered list would
    // show a country that does not match what was typed, and would hide the
    // "nothing matches" message behind a stale row.
    if (!selected) return slice;
    if (slice.some((country) => country.value === selected)) return slice;
    if (!matches.some((country) => country.value === selected)) return slice;

    const current = matches.find((country) => country.value === selected);
    return current ? [current, ...slice.slice(0, MAX_VISIBLE_COUNTRIES - 1)] : slice;
  }, [matches, selected]);

  const isTruncated = matches.length > MAX_VISIBLE_COUNTRIES;

  /**
   * Saves the country, and only then leaves the step.
   *
   * The write and both cache invalidations live in `useUpdatePayoutCountry`,
   * whose docstring carries the reasoning for each; the repository's carries
   * why the payload names only the country and leaves `state` and `city` alone.
   * This component's job is the question and the answer.
   */
  const { saveCountry, isSaving, saveError } = useUpdatePayoutCountry({
    onSuccess: onSaved,
  });

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: C.bg }}>
      <div className="flex justify-end px-5 pt-5 flex-shrink-0">
        <button
          onClick={onDismiss}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-full"
          style={{ backgroundColor: '#FFFFFF14' }}
        >
          <X className="w-4 h-4" style={{ color: C.muted }} />
        </button>
      </div>

      <div className="px-5 pt-2 flex-shrink-0">
        <h1
          data-testid="payout-country-title"
          className="text-2xl font-bold mb-2"
          style={{ color: C.text }}
        >
          {t('earnings.step1_select_country')}
        </h1>
        <p className="text-sm mb-3" style={{ color: C.muted }}>
          {t('earnings.country_step_body')}
        </p>
        <p
          className="text-xs mb-5 px-4 py-3 rounded-xl"
          style={{ color: C.text, backgroundColor: C.surface, border: `1px solid ${C.border}` }}
        >
          {t('earnings.country_step_note')}
        </p>

        {/* Search — 194 options is not a list you scroll on a phone. */}
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-2xl mb-3"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
        >
          <Search className="w-4 h-4 flex-shrink-0" style={{ color: C.muted }} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('earnings.country_step_search')}
            aria-label={t('earnings.country_step_search')}
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: C.text }}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-5">
        {visibleCountries.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: C.muted }}>
            {t('earnings.country_step_empty')}
          </p>
        ) : (
          <div
            className="rounded-2xl overflow-hidden"
            style={{ border: `1px solid ${C.border}` }}
            role="radiogroup"
            aria-label={t('earnings.country')}
          >
            {visibleCountries.map((country, i) => {
              const isSelected = selected === country.value;
              return (
                <button
                  key={country.value}
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelected(country.value)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  style={{
                    backgroundColor: isSelected
                      ? `${C.accent}22`
                      : i % 2 === 0
                        ? C.surface
                        : C.surface2,
                    minHeight: 44,
                  }}
                >
                  <span className="text-sm" style={{ color: C.text }}>{country.label}</span>
                  {isSelected && (
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: C.accent }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}

        {isTruncated && (
          <p className="text-xs text-center py-4" style={{ color: C.muted }}>
            {t('earnings.country_step_more', {
              shown: MAX_VISIBLE_COUNTRIES,
              total: matches.length,
            })}
          </p>
        )}
      </div>

      <div className="px-5 pb-8 pt-4 flex-shrink-0" style={{ backgroundColor: C.bg }}>
        {saveError && (
          <p role="alert" className="text-xs text-center mb-3" style={{ color: C.error }}>
            {t('earnings.country_step_error')}
          </p>
        )}
        <button
          data-testid="payout-country-continue"
          onClick={() => saveCountry(selected)}
          disabled={!selected || isSaving}
          className="w-full py-4 rounded-full font-semibold text-sm text-white transition-opacity"
          style={{
            backgroundColor: C.accent,
            opacity: selected && !isSaving ? 1 : 0.4,
          }}
        >
          {isSaving ? t('earnings.country_step_saving') : t('earnings.continue')}
        </button>
      </div>
    </div>
  );
};

export default PayoutCountryStep;
